import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/shared/ui";
import { useLocale } from "@/shared/hooks";
import { AuditEntryItem } from "@/shared/components";
import { useGetTaskAuditQuery } from "../../../api";
import { buildTasksAuditConfig, isReorderEntry } from "../../../utils/auditHelpers";

interface Props {
  taskId: number;
}

function HistorySkeleton() {
  return (
    <div className="flex flex-col gap-4 p-1">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton circle width={28} height={28} />
          <div className="flex-1 flex flex-col gap-1">
            <Skeleton height={14} width={140} />
            <Skeleton height={12} width={100} />
            <Skeleton circle width={24} height={24} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TaskHistory({ taskId }: Props) {
  const { t } = useTranslation("TasksPage");
  const { t: tProjects } = useTranslation("ProjectsPage");
  const locale = useLocale();
  const { data, isLoading } = useGetTaskAuditQuery(taskId, { refetchOnMountOrArgChange: true });

  const config = useMemo(() => buildTasksAuditConfig(t, tProjects, locale), [t, tProjects, locale]);

  if (isLoading) {
    return <HistorySkeleton />;
  }

  const entries = (data?.results ?? []).filter((e) => !isReorderEntry(e));

  if (entries.length === 0) {
    return (
      <div className="p-1">
        <p className="text-body-regular-sm content-base-secondary">{t("history.empty")}</p>
      </div>
    );
  }

  return (
    <div className="p-1">
      {entries.map((entry, index) => (
        <AuditEntryItem
          key={entry.id}
          entry={entry}
          isLast={index === entries.length - 1}
          namespace="history"
          config={config}
          t={t}
          locale={locale}
        />
      ))}
    </div>
  );
}
